import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { JsonLd } from "@/components/JsonLd";
import { NotFoundView } from "@/components/NotFoundView";
import { PublicPageLayout } from "@/components/PublicPageLayout";
import { PublicFooter } from "@/components/public-layout";
import { formatBlogDate, getBlogPost } from "@/lib/blog";
import { articleSchema, breadcrumbListSchema } from "@/lib/schema";
import {
	buildLocalizedSeoLinks,
	getLocalizedSeoUrls,
	ogImageUrl,
} from "@/lib/seo";

export const Route = createFileRoute("/blog/$slug")({
	// The registry is a module constant, so resolving the post is synchronous and
	// the head can be built from it without a loader round trip.
	loader: ({ params }) => {
		if (!getBlogPost(params.slug)) throw notFound();
		return { slug: params.slug };
	},
	component: BlogPostPage,
	notFoundComponent: () => (
		<NotFoundView
			label="Post"
			description="That post doesn't exist."
			backHref="/blog"
			backLabel="Back to blog"
		/>
	),
	head: ({ matches, params }) => {
		const post = getBlogPost(params.slug);
		if (!post) return {};

		const path = `/blog/${post.slug}`;
		const seoUrls = getLocalizedSeoUrls({ matches, internalPath: path });
		const title = `${post.title} - AgentMRR`;

		return {
			meta: [
				{ title },
				{ name: "description", content: post.description },
				{ name: "keywords", content: post.tags.join(", ") },
				{ property: "og:title", content: title },
				{ property: "og:description", content: post.description },
				{ property: "og:image", content: ogImageUrl("blog") },
				{ property: "og:url", content: seoUrls.canonicalUrl },
				{ property: "og:type", content: "article" },
				{
					property: "og:locale",
					content: post.lang === "nl" ? "nl_NL" : "en_US",
				},
				{ property: "article:published_time", content: post.published },
				{ property: "article:modified_time", content: post.modified },
				{ property: "article:author", content: "AgentMRR" },
				{ name: "twitter:card", content: "summary_large_image" },
				{ name: "twitter:image", content: ogImageUrl("blog") },
			],
			links: buildLocalizedSeoLinks({ matches, internalPath: path }),
		};
	},
});

function BlogPostPage() {
	const { slug } = Route.useLoaderData();
	const post = getBlogPost(slug);
	if (!post) return null;

	const Body = post.body;

	return (
		<PublicPageLayout>
			<article
				className="mx-auto w-full max-w-3xl py-10"
				lang={post.lang ?? "en"}
			>
				<JsonLd
					data={breadcrumbListSchema([
						{ name: "Home", path: "/" },
						{ name: "Blog", path: "/blog" },
						{ name: post.title, path: `/blog/${post.slug}` },
					])}
				/>
				<JsonLd
					data={articleSchema({
						title: post.title,
						description: post.description,
						path: `/blog/${post.slug}`,
						datePublished: post.published,
						dateModified: post.modified,
					})}
				/>

				<Link
					to="/blog"
					className="inline-flex items-center gap-1.5 font-mono text-sm uppercase tracking-[0.12em] text-muted-foreground transition-colors hover:text-brand"
				>
					<ArrowLeft className="size-3" />
					Blog
				</Link>

				<h1
					className="mt-4 text-balance font-mono text-[clamp(1.5rem,3vw,2rem)] leading-[1.1] tracking-[-0.02em] text-foreground"
					style={{ fontWeight: 400 }}
				>
					{post.title}
				</h1>
				<p className="mt-3 font-mono text-sm uppercase tracking-[0.12em] text-muted-foreground">
					{formatBlogDate(post.published, post.lang)} &middot;{" "}
					{post.readingMinutes}{" "}
					{post.lang === "nl" ? "min leestijd" : "min read"}
				</p>

				<div className="mt-8">
					<Body />
				</div>

				<ul className="mt-10 flex flex-wrap gap-2">
					{post.tags.map((tag) => (
						<li
							key={tag}
							className="border border-border bg-card px-2.5 py-1 font-mono text-xs uppercase tracking-[0.1em] text-muted-foreground"
						>
							{tag}
						</li>
					))}
				</ul>
			</article>

			<PublicFooter />
		</PublicPageLayout>
	);
}
